import { useEffect, useState, useRef, useCallback } from "react";
import { ref, onValue } from "firebase/database";
import { database } from "../config/firebaseConfig";
import axios from "../config/axios";
import useGlobalStore from "../store/global.store";

export function useBeaconControl() {
  const device = useGlobalStore((s) => s.device);
  const hasAddon = useGlobalStore((s) => s.hasBeaconAddOn);

  // ===== Identity =====
  //
  // The lock no longer stores activation codes or Mongo ids (they were
  // world-readable). It stores an OPAQUE holderKey — an HMAC the server
  // derives from this display's activation_code. Same stability property
  // the old activation_code comparison had (survives resets/reconnects),
  // but worthless to anyone reading the node.
  //
  // We fetch OUR key once from the authenticated /esp/control-state
  // endpoint; the live onValue subscription below still gives instant
  // "another device took control" updates.
  const myDeviceId = device?._id ?? null;
  const myActivationCode = device?.activation_code ?? null;
  const beaconDeviceId = device?.magic_beacon_device?._id ?? null;

  const [myKey, setMyKey] = useState(null);
  const [keyReady, setKeyReady] = useState(false); // control-state fetch settled
  const [holder, setHolder] = useState(null);
  const [synced, setSynced] = useState(false);

  // Fetch this display's opaque key (and initial holder snapshot).
  useEffect(() => {
    if (!beaconDeviceId || !myDeviceId) {
      setMyKey(null);
      return;
    }
    let cancelled = false;
    axios
      .post(
        "/esp/control-state",
        { deviceId: beaconDeviceId, displayDeviceId: myDeviceId },
        { withCredentials: true },
      )
      .then((res) => {
        if (!cancelled && res.data && !res.data.err) {
          setMyKey(res.data.myKey || null);
        }
        if (!cancelled) setKeyReady(true);
      })
      .catch((e) => {
        console.error("[useBeaconControl] control-state failed:", e);
        if (!cancelled) setKeyReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [beaconDeviceId, myDeviceId]);

  // Live lock subscription (reads stay open per-key in RTDB rules).
  useEffect(() => {
    if (!beaconDeviceId) {
      setHolder(null);
      setSynced(false);
      return;
    }
    const lockRef = ref(database, `beaconControl/${beaconDeviceId}`);
    const unsub = onValue(lockRef, (snap) => {
      setHolder(snap.val() || null);
      setSynced(true);
    });
    return () => {
      unsub();
      setSynced(false);
    };
  }, [beaconDeviceId]);

  // ===== isMe =====
  // New locks carry holderKey. During migration, locks written by the old
  // client may still carry holderActivationCode / holderDeviceId — accept
  // those matches too so existing holders aren't evicted. (Once the node
  // is wiped post-deploy, only holderKey locks exist.)
  const isMe = !!(
    holder &&
    ((myKey && holder.holderKey === myKey) ||
      (!holder.holderKey &&
        myActivationCode &&
        holder.holderActivationCode === myActivationCode) ||
      (!holder.holderKey &&
        !holder.holderActivationCode &&
        myDeviceId &&
        holder.holderDeviceId === myDeviceId))
  );
  const isController = isMe && hasAddon;
  const hasHolder = !!holder;

  const claim = useCallback(async () => {
    if (!beaconDeviceId) return { ok: false, reason: "no_beacon" };
    if (!myDeviceId) return { ok: false, reason: "no_device" };
    if (!hasAddon) return { ok: false, reason: "no_addon" };
    try {
      const res = await axios.post(
        "/esp/control-claim",
        { deviceId: beaconDeviceId, displayDeviceId: myDeviceId },
        { withCredentials: true },
      );
      if (res.data && !res.data.err) {
        // Optimistic local update; onValue confirms momentarily.
        if (res.data.myKey) setMyKey(res.data.myKey);
        if (res.data.holder) setHolder(res.data.holder);
        return { ok: true };
      }
      return { ok: false, reason: "write_failed" };
    } catch (e) {
      console.error("[useBeaconControl] claim failed:", e);
      return { ok: false, reason: "write_failed" };
    }
  }, [beaconDeviceId, myDeviceId, hasAddon]);

  const release = useCallback(async () => {
    if (!isMe) return { ok: false, reason: "not_holder" };
    return { ok: false, reason: "sole_holder" };
  }, [isMe]);

  const ensureControl = useCallback(async () => {
    if (isController) return { ok: true };
    if (!hasAddon) return { ok: false, reason: "no_addon" };
    if (!hasHolder) {
      const r = await claim();
      return r.ok ? { ok: true, claimed: true } : { ok: false, reason: r.reason };
    }
    return { ok: false, reason: "other_holder", holder };
  }, [isController, hasAddon, hasHolder, holder, claim]);

  // Addon lost while holding -> clear the lock (server-mediated now).
  const lastAddonRef = useRef(hasAddon);
  useEffect(() => {
    const prev = lastAddonRef.current;
    lastAddonRef.current = hasAddon;
    if (prev === true && hasAddon === false && isMe && beaconDeviceId && myDeviceId) {
      console.log("[useBeaconControl] addon lost while holder — clearing lock");
      axios
        .post(
          "/esp/control-clear",
          { deviceId: beaconDeviceId, displayDeviceId: myDeviceId },
          { withCredentials: true },
        )
        .catch((e) =>
          console.error("[useBeaconControl] control-clear failed:", e),
        );
    }
  }, [hasAddon, isMe, beaconDeviceId, myDeviceId]);

  return {
    isController,
    hasHolder,
    holder,
    isMe,
    hasAddon,
    synced,
    keyReady,
    claim,
    release,
    ensureControl,
    beaconDeviceId,
    myDeviceId,
    myActivationCode,
  };
}

export default useBeaconControl;
